import { createStore } from "solid-js/store";

import { CookieKey, LocalStorageKey, StorageType } from "@/constants/constants";
import { getLocalStorageItem, setLocalStorageItem } from "@/lib/localStorage";
import { urlAddParam } from "@/lib/utils";
import { cookieStorage } from "@solid-primitives/storage";

import {
  deviceInfo,
  getDeviceObject,
  getOsType,
  isOpenByStandalone,
  updateDeviceInfo,
} from "../stores/deviceStore";

export type UrlInfo = {
  params: Record<string, string>;
  url: string;
  reffer: {
    url: string;
    referer: string;
  }[];
};
const [state, setState] = createStore<UrlInfo>({
  params: {},
  url: "",
  reffer: [],
});

/** 同源 OAuth 静态页（如 Google 登录 iframe 内若误跑 parseUrl），不应覆盖归因落地 url */
function isAttributionNoiseDocumentUrl(rawUrl: string): boolean {
  try {
    return new URL(rawUrl).pathname.toLowerCase().includes("googlelogin");
  } catch {
    return false;
  }
}

export function useUrlStorage(
  storageKey = LocalStorageKey.MyPageParams,
  storageType = StorageType.Local,
) {
  const storage =
    storageType === StorageType.Local ? localStorage : sessionStorage;

  const parseUrl = (url: string) => {
    tryRedirect(url);
    saveLastUrl(url);

    try {
      const urlObj = new URL(url);
      const persistedBefore = getStoredParams() as Partial<UrlInfo>;
      if (
        isAttributionNoiseDocumentUrl(url) &&
        typeof persistedBefore.url === "string" &&
        persistedBefore.url.length > 0 &&
        !isAttributionNoiseDocumentUrl(persistedBefore.url)
      ) {
        const prev = persistedBefore as UrlInfo;
        setState(prev);
        return prev;
      }

      const info: UrlInfo = {
        params: {},
        url: "",
        reffer: [],
      };
      const rcsEntryTime = getLocalStorageItem(LocalStorageKey.RCSEntryTime, 0);
      if (rcsEntryTime == 0) {
        setLocalStorageItem(LocalStorageKey.RCSEntryTime, new Date().getTime());
      }

      info.url = url;
      const fbInitData: Record<string, string> = getLocalStorageItem(
        LocalStorageKey.FacebookInit,
        {},
      );
      urlObj.searchParams.forEach((value, key) => {
        if (key === "ch") {
          localStorage.setItem(LocalStorageKey.LastChannel, value);
        }
        if (key === "fbclid") {
          fbInitData.has_fbclid = "1";
          fbInitData.fbclid = value;
        }
        if (key === "fb_fbc") {
          fbInitData.has_fb_fbc = "1";
          fbInitData.fb_fbc = value;
        }
        if (key === "fb_fbp") {
          fbInitData.has_fb_fbp = "1";
          fbInitData.fb_fbp = value;
        }

        info.params[key] = value;
      });

      setLocalStorageItem(LocalStorageKey.FacebookInit, fbInitData);

      info.reffer = [
        {
          url: url,
          referer:
            urlObj.searchParams.get("_referer") || window.document.referrer,
        },
      ];

      const storgeInfo = getStoredParams();

      //如果url和本地存储的url不一致，并且有参数，则更新url
      if (
        storgeInfo.url &&
        storgeInfo.url !== url &&
        urlObj.searchParams.toString()
      ) {
        if (storgeInfo.reffer?.length >= 5) {
          //如果本地存储的reffer长度大于5，则删除第一个，并添加新的reffer
          info.reffer = [
            ...(storgeInfo.reffer || []).slice(1),
            {
              url: url,
              referer:
                urlObj.searchParams.get("_referer") || window.document.referrer,
            },
          ];
        } else {
          info.reffer = [
            ...(storgeInfo?.reffer || []),
            {
              url: url,
              referer:
                urlObj.searchParams.get("_referer") || window.document.referrer,
            },
          ];
        }
        //如果url不一致，并且没有参数，则不更新url 归因参数取上次归因的参数
      } else if (
        storgeInfo.url &&
        storgeInfo.url !== url &&
        !urlObj.searchParams.toString()
      ) {
        info.url = storgeInfo.url;
        info.reffer = [...(storgeInfo?.reffer || [])];
        info.params = storgeInfo.params;
      } else if (
        //如果url一致，并且有参数，则更新url 归因参数取当前的参数
        storgeInfo.url &&
        storgeInfo.url === url &&
        urlObj.searchParams.toString()
      ) {
        info.url = url;
        info.reffer = [...(storgeInfo?.reffer || [])];
      }

      // 存储到指定的 storage
      storage.setItem(storageKey, JSON.stringify(info));
      setState(info);
      updateDeviceStoreFromParams();
      return info;
    } catch (error) {
      console.error("Failed to parse URL:", error);
      return {};
    }
  };

  const tryRedirect = (url: string) => {
    const urlObj = new URL(url);
    const hostname = window.location.hostname;
    const mainDomain = "afun.bet.br";
    const isPwa = urlObj.searchParams.get("mApp") == "1";
    const isTwa = urlObj.searchParams.get("mApp") == "14";
    let ch = "";
    // 匹配 "数字.afun.bet.br"
    const regex = /^(\d+)\.afun\.bet\.br$/;

    const match = hostname.match(regex);
    if (match) {
      ch = match[1];
    }
    let targetUrl = "";
    if (
      window.location.hostname.includes("afun.bet.br") &&
      regex.test(hostname) &&
      !isPwa &&
      !isTwa
    ) {
      targetUrl =
        window.location.protocol +
        "//" +
        mainDomain +
        window.location.pathname +
        window.location.search;
    }

    const localToken = getLocalStorageItem(LocalStorageKey.TOKEN, "");
    if (localToken) {
      targetUrl = urlAddParam(targetUrl, `cToken=${localToken}`);
    }
    if (ch) {
      targetUrl = urlAddParam(targetUrl, `ch=${ch}`);
    }
    if (targetUrl) {
      window.location.href = targetUrl;
    }
  };

  /** 获取客户端 IP 地址 */
  const getClientIP = async (): Promise<string> => {
    try {
      // 使用第三方服务获取 IP
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip || "";
    } catch (error) {
      console.error("Failed to get client IP:", error);
      return "";
    }
  };

  /** 发送 PWA 打开事件到 mgskyads */
  const sendPWAOpenEvent = async (
    pixelClickId: string | null,
    pixelId: string | null,
  ) => {
    try {
      const ip = await getClientIP();
      const requestBody = {
        eid: "EVENT_PWA_OPEN",
        pixel_click_id: pixelClickId || "",
        pixel_id: pixelId || "",
        ip: ip,
        data: {},
      };

      await fetch("https://s.mgskyads.com/e/s2s", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
    } catch (error) {
      console.error("Failed to send PWA open event:", error);
    }
  };

  const saveLastUrl = (url: string) => {
    if (!url) {
      return;
    }
    const urlObj = new URL(url);

    const isPWAFirstOpen = getLocalStorageItem(
      LocalStorageKey.IsPWAFirstOpen,
      "0",
    );

    const isTWAFirstOpen = urlObj.searchParams.get("first_open") == "1";
    const mApp = urlObj.searchParams.get("mApp");
    const ctoken = urlObj.searchParams.get("cToken") || "";

    // 第一次打开 PWA (mApp=1) 时发送事件
    if (isPWAFirstOpen == "0" && mApp == "1") {
      const pixelClickId = urlObj.searchParams.get("pixel_click_id");
      const pixelId = urlObj.searchParams.get("pixel_id");
      if (pixelClickId && pixelId) {
        // 异步发送事件，不阻塞主流程
        sendPWAOpenEvent(pixelClickId, pixelId).catch((error) => {
          console.error("Error sending PWA open event:", error);
        });
      }
    }

    if (ctoken) {
      if (
        //h5或第一次打开pwa
        isPWAFirstOpen == "0" &&
        mApp == "1"
      ) {
        setLocalStorageItem(LocalStorageKey.TOKEN, ctoken);
        return;
      }
      if (
        //h5或第一次打开pwa
        isTWAFirstOpen &&
        urlObj.searchParams.get("mApp") == "14"
      ) {
        setLocalStorageItem(LocalStorageKey.TOKEN, ctoken);
        return;
      }
      if (
        deviceInfo.app_type == 4 || // RN支持自动登录
        deviceInfo.app_type == 0
      ) {
        setLocalStorageItem(LocalStorageKey.TOKEN, ctoken);
      }
    }
    console.log("isPWAFirstOpen-------------------", isPWAFirstOpen);
    setLocalStorageItem(LocalStorageKey.IsPWAFirstOpen, "1");
  };

  const saveCookieParams = () => {
    const url = getStoredParams()?.url;
    if (!url) {
      return;
    }
    const fbInitData: Record<string, string> = getLocalStorageItem(
      LocalStorageKey.FacebookInit,
      {},
    );
    const urlObj = new URL(url);
    const ttclid = urlObj.searchParams.get("ttclid") || "";

    const cookieTtclid = cookieStorage.getItem(CookieKey.TIKTOK_CLID) || "";
    if (!ttclid && cookieTtclid) {
      urlObj.searchParams.set("ttclid", cookieTtclid);
    }

    if (ttclid && cookieTtclid !== ttclid) {
      cookieStorage.setItem(CookieKey.TIKTOK_CLID, ttclid);
    }

    const tt_ttp = urlObj.searchParams.get("_ttp") || "";
    const cookie_ttp = cookieStorage.getItem(CookieKey.TIKTOK_TTP) || "";
    if (!tt_ttp && cookie_ttp) {
      urlObj.searchParams.set("tt_ttp", cookie_ttp);
    }
    if (tt_ttp && cookie_ttp !== tt_ttp) {
      cookieStorage.setItem(CookieKey.TIKTOK_TTP, tt_ttp);
    }

    const fb_fbc = urlObj.searchParams.get("fb_fbc") || "";
    const cookie_fb_fbc = cookieStorage.getItem(CookieKey.FB_FBC) || "";
    if (!fb_fbc && cookie_fb_fbc) {
      urlObj.searchParams.set("fb_fbc", cookie_fb_fbc);
    }
    if (fb_fbc && cookie_fb_fbc !== fb_fbc) {
      cookieStorage.setItem(CookieKey.FB_FBC, fb_fbc);
    }
    if (!cookie_fb_fbc) {
      fbInitData.has_cookie_fb_fbc = "0";
      setLocalStorageItem(LocalStorageKey.FacebookInit, fbInitData);
    } else {
      fbInitData.has_cookie_fb_fbc = "1";
      fbInitData.cookie_fb_fbc = cookie_fb_fbc;
      setLocalStorageItem(LocalStorageKey.FacebookInit, fbInitData);
    }

    const fb_fbp = urlObj.searchParams.get("fb_fbp") || "";
    const cookie_fb_fbp = cookieStorage.getItem(CookieKey.FB_FBP) || "";
    if (!fb_fbp && cookie_fb_fbp) {
      urlObj.searchParams.set("fb_fbp", cookie_fb_fbp);
    }
    if (fb_fbp && cookie_fb_fbp !== fb_fbp) {
      cookieStorage.setItem(CookieKey.FB_FBP, fb_fbp);
    }
    if (!cookie_fb_fbp) {
      fbInitData.has_cookie_fb_fbp = "0";
      setLocalStorageItem(LocalStorageKey.FacebookInit, fbInitData);
    } else {
      fbInitData.has_cookie_fb_fbp = "1";
      fbInitData.cookie_fb_fbp = cookie_fb_fbp;
      setLocalStorageItem(LocalStorageKey.FacebookInit, fbInitData);
    }
    const myPageParams: UrlInfo = getLocalStorageItem(
      LocalStorageKey.MyPageParams,
      {
        params: {},
        url: "",
        reffer: [],
      },
    );
    if (myPageParams.url) {
      myPageParams.url = urlObj.toString();
      setLocalStorageItem(LocalStorageKey.MyPageParams, myPageParams);
    }
  };

  const getStoredParams = () => {
    try {
      // 如果内存中没有，则从 storage 中获取
      const stored = storage.getItem?.(storageKey);
      if (stored) {
        const parsedData = JSON.parse(stored);
        // 将数据同步到内存中
        return parsedData;
      }
      return {};
    } catch (error) {
      console.error("Failed to get stored params:", error);
      return {};
    }
  };

  const clearStoredParams = () => {
    storage.removeItem(storageKey);
    setState({});
  };

  const getParamByKey = (key: string): string | undefined => {
    try {
      // 如果内存中没有，则从 storage 中获取
      const stored = storage.getItem?.(storageKey);
      if (stored) {
        const parsedData = JSON.parse(stored);
        // 将数据同步到内存中
        return parsedData.params?.[key];
      }
      return undefined;
    } catch (error) {
      console.error(`Failed to get param by key ${key}:`, error);
      return undefined;
    }
  };

  const updateDeviceStoreFromParams = async () => {
    try {
      const params = getStoredParams()?.params || {};
      const deviceUpdates: Record<string, unknown> = {};

      if (isOpenByStandalone()) {
        deviceUpdates.app_type = 1;
      }
      // const manifest = await fetch("/manifest.json");
      // const manifestData = await manifest.json();
      /* prettier-ignore */
      // const startUrl = new URLSearchParams(manifestData.start_url.split("/?")[1]);
      // const mAppByStartUrl = startUrl.get("mApp");
      // const mApp = params["mApp"] || mAppByStartUrl;
      const mApp = params["mApp"]
      if (mApp) {
        deviceUpdates.app_type = Number(mApp);
      }

      let referrer = params["_referer"];
      const userAgent = navigator.userAgent;
      const pwa = deviceInfo.app_type == 1 || mApp == "1" || mApp == "2";

      if (deviceInfo.ios) {
        if (/(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent)) {
          deviceUpdates.iosWebView = true;
          deviceUpdates.app_type = 4;
        }
      }
      if (deviceInfo.appShell !== "Browser") {
        //外部app
        deviceUpdates.app_type = 4;
      }
      // else if (mApp == '5') {
      // 	//马甲包【上架不稳定包】
      // 	deviceInfo.app_type = 5
      // }
      deviceUpdates.app =
        deviceUpdates.app_type == 3 ||
        mApp == "3" ||
        mApp == "10" ||
        deviceUpdates.app_type == 10 ||
        mApp == "14" ||
        deviceUpdates.app_type == 14;
      deviceUpdates.webview =
        deviceUpdates.app_type == 3 ||
        deviceUpdates.app_type == 4 ||
        mApp == "3" ||
        mApp == "4" ||
        deviceUpdates.app_type == 10 ||
        mApp == "10";
      // || mApp == '5'

      if (!deviceUpdates.webview && pwa) {
        deviceUpdates.pwa = true;
      }

      if (referrer) {
        // Base64.decode 返回的是 number[]，需要转换为字符串
        const decodedBytes = window.atob(referrer);
        referrer = decodedBytes;
        deviceUpdates.referrer = referrer;
      }
      deviceUpdates.pwa = pwa;
      // 根据 URL 参数更新设备信息
      if (mApp) {
        deviceUpdates.m_app = Number(mApp);
      }
      if (deviceInfo.webview) {
        deviceUpdates.client_type = 1;
      }
      if (deviceUpdates.webView || deviceUpdates.pwa) {
        deviceUpdates.client_type = 1;
      }
      deviceUpdates.device_id = getDeviceObject().device_id;

      deviceUpdates.os_type = getOsType(userAgent, deviceInfo.pc);
      // 如果有需要更新的设备信息，则更新
      if (Object.keys(deviceUpdates).length > 0) {
        updateDeviceInfo(deviceUpdates);
      }
    } catch (error) {
      console.error("Failed to update device store from params:", error);
    }
  };

  return {
    parseUrl,
    getStoredParams,
    saveCookieParams,
    clearStoredParams,
    getParamByKey,
    updateDeviceStoreFromParams,
    params: state,
  };
}
